/* eslint-disable */
/**
 * Remove Pricing All Types Validation Functions
 * Contains all validation logic for Remove Pricing All Types Excel uploads
 */

import { isValidInput, isNotValidInput, tranformDate, appendComment } from './validationUtils';
import {
  RemovePricingAllTypesIndices,
  ExcelData,
  ValidationStatusFlagSetter
} from './validationTypes';
import { ALLOWED_RMV_PRC_TYPES_ALL } from '../../../constants/constants';

/**
 * Main validation function for Remove Pricing All Types Excel data
 * 
 * @param data - Excel data array (2D array with headers in row 0)
 * @param EXCEL_HEADER_RMVPRC_ALLTYPES_INDEX - Column indices for Remove Pricing All Types
 * @param validationStatusFlagSetter - Function to set validation status flag
 * @returns Promise resolving to validated data array
 */
export const validateRemovePricingAllTypes = async (
  data: ExcelData,
  EXCEL_HEADER_RMVPRC_ALLTYPES_INDEX: RemovePricingAllTypesIndices,
  validationStatusFlagSetter: ValidationStatusFlagSetter
): Promise<ExcelData> => {
    console.log('in validate Remove Pricing All Types');

    // Destructure column indices
    const {
      ZONE_RMVPRC_AT_IND,
      TYPE_RMVPRC_AT_IND,
      DEPARTMENT_RMVPRC_AT_IND,
      SUB_DEPARTMENT_RMVPRC_AT_IND,
      BRACKET_RMVPRC_AT_IND,
      RMS_RMVPRC_AT_IND,
      ALL_PRICING_PARAMS_RMVPRC_AT_IND,
      WEEKLY_RATE_TERM_RMVPRC_AT_IND,
      ADDON_WEEKLY_RATE_RMVPRC_AT_IND,
      CASH_PRICE_RMVPRC_AT_IND,
      SAC_DAYS_RMVPRC_AT_IND,
      EPO_PERCENTAGE_RMVPRC_AT_IND,
      MANUAL_PO_TURNS_RMVPRC_AT_IND,
      CLEARANCE_RMVPRC_AT_IND,
      FROM_RANGE_RMVPRC_AT_IND,
      TO_RANGE_RMVPRC_AT_IND,
      VALIDATION_STATUS_RMVPRC_AT_IND,
      VALIDATION_COMMENT_RMVPRC_AT_IND,
      UPLOAD_STATUS_RMVPRC_AT_IND,
      UPLOAD_COMMENTS_RMVPRC_AT_IND
    } = EXCEL_HEADER_RMVPRC_ALLTYPES_INDEX;

    let validationFlag = true;

    // Price parameter column indices for convenience
    const priceParamIndices = [
      ALL_PRICING_PARAMS_RMVPRC_AT_IND,
      WEEKLY_RATE_TERM_RMVPRC_AT_IND,
      ADDON_WEEKLY_RATE_RMVPRC_AT_IND,
      CASH_PRICE_RMVPRC_AT_IND,
      SAC_DAYS_RMVPRC_AT_IND,
      EPO_PERCENTAGE_RMVPRC_AT_IND,
      MANUAL_PO_TURNS_RMVPRC_AT_IND,
      CLEARANCE_RMVPRC_AT_IND
    ];

    // Phase 1 — Duplicate detection
    const seenRows = new Set<string>();

    for (let rowindex = 1; rowindex < data.length; rowindex++) {
      if (data[rowindex].length == 0) {
        break;
      }

      const row = data[rowindex];
      const rowIdent = [
        row[ZONE_RMVPRC_AT_IND],
        row[TYPE_RMVPRC_AT_IND],
        row[DEPARTMENT_RMVPRC_AT_IND],
        row[SUB_DEPARTMENT_RMVPRC_AT_IND],
        row[BRACKET_RMVPRC_AT_IND],
        row[RMS_RMVPRC_AT_IND],
        row[ALL_PRICING_PARAMS_RMVPRC_AT_IND],
        row[WEEKLY_RATE_TERM_RMVPRC_AT_IND],
        row[ADDON_WEEKLY_RATE_RMVPRC_AT_IND],
        row[CASH_PRICE_RMVPRC_AT_IND],
        row[SAC_DAYS_RMVPRC_AT_IND],
        row[EPO_PERCENTAGE_RMVPRC_AT_IND],
        row[MANUAL_PO_TURNS_RMVPRC_AT_IND],
        row[CLEARANCE_RMVPRC_AT_IND],
        row[FROM_RANGE_RMVPRC_AT_IND],
        row[TO_RANGE_RMVPRC_AT_IND]
      ].map(value => (typeof value === 'string' ? value.trim() : value))
        .filter(value => value !== undefined && value !== null && value !== '')
        .join('|');

      if (rowIdent != '') {
        if (seenRows.has(rowIdent)) {
          row[VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
          row[VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(row[VALIDATION_COMMENT_RMVPRC_AT_IND], 'This record is a duplicate of another record in this upload file.');
          validationFlag = false;
        } else {
          seenRows.add(rowIdent);
        }
      }
    }

    // Phase 2 — Row-level transformation and validation loop
    for (let rowindex = 1; rowindex < data.length; rowindex++) {
      if (data[rowindex].length == 0) {
        break;
      }

      // Date transformation (From Range)
      if (isValidInput(data[rowindex][FROM_RANGE_RMVPRC_AT_IND])) {
        const fromValue = data[rowindex][FROM_RANGE_RMVPRC_AT_IND];
        if (typeof fromValue === 'number' && !isNaN(fromValue) && fromValue > 0) {
          data[rowindex][FROM_RANGE_RMVPRC_AT_IND] = tranformDate(fromValue);
        }
      }

      // Date transformation (To Range)
      if (isValidInput(data[rowindex][TO_RANGE_RMVPRC_AT_IND])) {
        const toValue = data[rowindex][TO_RANGE_RMVPRC_AT_IND];
        if (typeof toValue === 'number' && !isNaN(toValue) && toValue > 0) {
          data[rowindex][TO_RANGE_RMVPRC_AT_IND] = tranformDate(toValue);
        }
      }

      // Price parameter normalization
      for (const paramIndex of priceParamIndices) {
        data[rowindex][paramIndex] = isValidInput(data[rowindex][paramIndex])
          ? data[rowindex][paramIndex].toString().trim().toUpperCase()
          : 'NO';
      }

      // Skip duplicate records - they've already been marked as failed
      if (data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] === 'Failed' &&
          data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND]?.includes('This record is a duplicate of another record in this upload file.')) {
        continue;
      }

      // Initialize validation status
      data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Passed';
      data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = undefined;
      data[rowindex][UPLOAD_STATUS_RMVPRC_AT_IND] = undefined;
      data[rowindex][UPLOAD_COMMENTS_RMVPRC_AT_IND] = undefined;

      // Rule 1 — Zone validation
      if (isNotValidInput(data[rowindex][ZONE_RMVPRC_AT_IND]) || isNaN(data[rowindex][ZONE_RMVPRC_AT_IND])) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Zone Number is required');
      }

      // Rule 2 — Type validation
      if (isNotValidInput(data[rowindex][TYPE_RMVPRC_AT_IND]) ||
          !ALLOWED_RMV_PRC_TYPES_ALL.includes(data[rowindex][TYPE_RMVPRC_AT_IND]?.toString().trim().toUpperCase())) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Type is required');
      }

      // Rule 3 — Hierarchy: at least Dept or RMS
      if (isNotValidInput(data[rowindex][DEPARTMENT_RMVPRC_AT_IND]) &&
          isNotValidInput(data[rowindex][RMS_RMVPRC_AT_IND])) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Hierarchy information is required. Please provide the following data: Department (conditional), Sub-department (optional), Bracket (optional), and RMS Number (optional)');
      }

      // Rule 4 — SubDept requires Dept
      if (isValidInput(data[rowindex][SUB_DEPARTMENT_RMVPRC_AT_IND]) &&
          isNotValidInput(data[rowindex][DEPARTMENT_RMVPRC_AT_IND])) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Department is required when a Sub-department is provided.');
      }

      // Rule 5 — Bracket requires Dept + SubDept
      if (isValidInput(data[rowindex][BRACKET_RMVPRC_AT_IND]) &&
          (isNotValidInput(data[rowindex][DEPARTMENT_RMVPRC_AT_IND]) || isNotValidInput(data[rowindex][SUB_DEPARTMENT_RMVPRC_AT_IND]))) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Department and Sub-department are required when a Bracket is provided.');
      }

      // Rule 6 — RMS excludes hierarchy
      if (isValidInput(data[rowindex][RMS_RMVPRC_AT_IND]) &&
          (isValidInput(data[rowindex][DEPARTMENT_RMVPRC_AT_IND]) ||
           isValidInput(data[rowindex][SUB_DEPARTMENT_RMVPRC_AT_IND]) ||
           isValidInput(data[rowindex][BRACKET_RMVPRC_AT_IND]))) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'RMS Number does not require other hierarchy level information.');
      }

      // Rule 7 — At least one price param YES
      const hasAnyPriceParam = priceParamIndices.some(idx => data[rowindex][idx] === 'YES');
      if (!hasAnyPriceParam) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Price parameter information is required. Please select specific price parameters or choose \'All Pricing Parameters\'.');
      }

      // Rule 8 — All Pricing Parameters exclusivity
      if (data[rowindex][ALL_PRICING_PARAMS_RMVPRC_AT_IND] === 'YES') {
        const individualParamIndices = [
          WEEKLY_RATE_TERM_RMVPRC_AT_IND,
          ADDON_WEEKLY_RATE_RMVPRC_AT_IND,
          CASH_PRICE_RMVPRC_AT_IND,
          SAC_DAYS_RMVPRC_AT_IND,
          EPO_PERCENTAGE_RMVPRC_AT_IND,
          MANUAL_PO_TURNS_RMVPRC_AT_IND,
          CLEARANCE_RMVPRC_AT_IND
        ];
        const hasIndividualParam = individualParamIndices.some(idx => data[rowindex][idx] === 'YES');
        if (hasIndividualParam) {
          data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'The specific price parameters should not be selected when \'All Pricing Parameters\' is selected.');
        }
      }

      // Get type value for type-specific rules
      const typeValue = isValidInput(data[rowindex][TYPE_RMVPRC_AT_IND])
        ? data[rowindex][TYPE_RMVPRC_AT_IND].toString().trim().toUpperCase()
        : '';

      // Rule 9 — Add-On Weekly Rate not for MANUAL
      if (typeValue === 'MANUAL' && data[rowindex][ADDON_WEEKLY_RATE_RMVPRC_AT_IND] === 'YES') {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Add-On Weekly Rate should not be selected for MANUAL pricing type.');
      }

      // Rule 10 — Manual PO Turns not for TEMP/PERM
      if ((typeValue === 'TEMPORARY' || typeValue === 'PERMANENT') && data[rowindex][MANUAL_PO_TURNS_RMVPRC_AT_IND] === 'YES') {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Manual PO Turns should not be selected for TEMPORARY or PERMANENT pricing type.');
      }

      // Rule 11 — Clearance not for MANUAL
      if (typeValue === 'MANUAL' && data[rowindex][CLEARANCE_RMVPRC_AT_IND] === 'YES') {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'Clearance should not be selected for MANUAL pricing type.');
      }

      // Rule 12 — From Range mandatory
      if (isNotValidInput(data[rowindex][FROM_RANGE_RMVPRC_AT_IND])) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'From Range is required.');
      } else if (!(data[rowindex][FROM_RANGE_RMVPRC_AT_IND] instanceof Date)) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'From Range is required.');
      }

      // Rule 13 — To Range mandatory
      if (isNotValidInput(data[rowindex][TO_RANGE_RMVPRC_AT_IND])) {
        data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
        data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'To Range is required.');
      }

      // Rule 14 — To Range not past date
      if (isValidInput(data[rowindex][TO_RANGE_RMVPRC_AT_IND]) && data[rowindex][TO_RANGE_RMVPRC_AT_IND] instanceof Date) {
        const toRangeDate = data[rowindex][TO_RANGE_RMVPRC_AT_IND];
        const uscurrentdate = new Date(new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })).setHours(0, 0, 0, 0));
        if (new Date(uscurrentdate.toDateString()) > new Date(toRangeDate.toDateString())) {
          data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'To Range cannot be a past date.');
        }
      }

      // Rule 15 — To Range >= From Range
      if (isValidInput(data[rowindex][FROM_RANGE_RMVPRC_AT_IND]) && data[rowindex][FROM_RANGE_RMVPRC_AT_IND] instanceof Date &&
          isValidInput(data[rowindex][TO_RANGE_RMVPRC_AT_IND]) && data[rowindex][TO_RANGE_RMVPRC_AT_IND] instanceof Date) {
        const fromRangeDate = data[rowindex][FROM_RANGE_RMVPRC_AT_IND];
        const toRangeDate = data[rowindex][TO_RANGE_RMVPRC_AT_IND];
        if (toRangeDate < fromRangeDate) {
          data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] = 'Failed';
          data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND] = appendComment(data[rowindex][VALIDATION_COMMENT_RMVPRC_AT_IND], 'To Range must be greater than or equal to \'From Range\'.');
        }
      }

      // After all rules, check if row failed and update validationFlag
      if (data[rowindex][VALIDATION_STATUS_RMVPRC_AT_IND] === 'Failed') {
        validationFlag = false;
      }
    }

    // Phase 3 — Set overall status
    validationStatusFlagSetter(validationFlag ? 'Validation Passed' : 'Validation Failed');

    console.log('Remove Pricing All Types validation completed. Status:', validationFlag ? 'Validation Passed' : 'Validation Failed');
    return data;
};
